import { CheckIcon } from "@heroicons/react/20/solid";
import {
  useMeeting,
  useParticipant,
  usePubSub,
} from "@videosdk.live/react-sdk";
import { useEffect, useMemo, useRef } from "react";
import { toast, Bounce } from "react-toastify";
import useIsMobile from "../utils/useIsMobile";
import useIsTab from "../utils/useIsTab";
import useIsBigDesktop from "../utils/useIsBigScreen";
import { useAppContext } from "../AppContextDef";
import { useTranslation } from "react-i18next";
import useIsLGDesktop from "../utils/useIsLGDesktop";
import { APP_FONT_STACK } from "../utils/common";
import usePubsubFunction from "../utils/usePubsubFunction";
import useFaceVerification from "../hooks/useFaceVerification";
import DeviceControls from "./DeviceControls";
const NETWORK_TOAST_ID = "bad-network-bitrate";
const LOW_BITRATE_KBPS = 30;

const ParticipantView = ({
  currentPage,
  capturedImage,
  setCapturedImage,
  setAgreed,
  agreed,
  abortSignal,
  videoHeight,
}) => {
  let isMobile = useIsMobile();
  let isTab = useIsTab();
  let isBigDesktop = useIsBigDesktop();
  let isLGDesktop = useIsLGDesktop();
  const pubsubPublishMethod = usePubsubFunction();
  const micRef = useRef(null);
  const videoRef = useRef(null);
  let isMobileOrTab = isMobile || isTab;

  // The recorder now owns the verification pipeline and is the sole publisher of
  // the verdict channels. The client only renders from those (via context, fed by
  // ClientPubSubHandler) and runs a single API as a fallback when delegated.
  const {
    selectedLanguageCode,
    isMeetingEnded,
    allDetails,
    faceVerified,
    agreedData,
    faceDetectError,
    isAbortSignal,
    delegatedApi,
    setFaceVerified,
    setAgreedData,
    setIsNetworkUnstable,
    recordVerificationOutcome,
  } = useAppContext();

  const { publish: delegatedApiSuccessPublish } = usePubSub(
    `DELEGATED_API_SUCCESS`,
  );
  // Delegated API results, forwarded to the recorder so IT can write the
  // IDENTITY log row. Unlike DELEGATED_API_SUCCESS this carries EVERY answered
  // attempt, not just the one that finally succeeded.
  const { publish: identityResultPublish } = usePubSub(`IDENTITY_RESULT`);

  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    changeLanguage(selectedLanguageCode);
  }, [selectedLanguageCode]);

  const { localParticipant } = useMeeting({});
  const {
    webcamStream,
    micStream,
    webcamOn,
    micOn,
    isLocal,
    captureImage,
    getVideoStats,
  } = useParticipant(localParticipant?.id);

  const fontFamilyCSS = APP_FONT_STACK;

  // This view remounts per page; start unverified (showing the "verifying"
  // overlay) until the recorder publishes fresh results for the new page.
  useEffect(() => {
    setFaceVerified(false);
    setAgreedData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the AbortController and the facematch reference frame fresh for the
  // delegated runner. capturedImage persists across page remounts in
  // MeetingContainer, so we mirror it into a ref.
  const abortRef = useRef(abortSignal);
  useEffect(() => {
    abortRef.current = abortSignal;
  }, [abortSignal]);

  const localRefImageRef = useRef(capturedImage);
  useEffect(() => {
    localRefImageRef.current = capturedImage;
  }, [capturedImage]);

  const faceVerification = useFaceVerification({
    mode: "delegate",
    captureImage,
    webcamOn,
    token: allDetails?.videoSDKToken,
    roomId: allDetails?.videoSDKRoomId,
    abortSignalRef: abortRef,
    currentPage,
    referenceImageRef: localRefImageRef,
    callbacks: {
      // In delegate mode this only fires for calls the recorder handed off. The
      // client never writes logs itself (it would need journey credentials and
      // would split the log across two writers), so forward the raw body and
      // let the recorder build and POST the row.
      onApiResult: ({ apiName, body, page }) =>
        pubsubPublishMethod(
          identityResultPublish,
          "IdentityResult",
          // Deliberately NOT persisted: every message causes a database write,
          // so replaying history to a reloaded recorder would duplicate rows.
          // There is no late-joiner to cover — the recorder subscribes at mount
          // and delegation cannot happen until its pipeline has already run.
          { persist: false },
          { apiName, body, page },
        ),
    },
  });

  // Mirror the recorder's verdict into the page-local `agreed` state that gates
  // the "I Agree" button.
  useEffect(() => {
    setAgreed(!!agreedData);
  }, [agreedData]);

  // Capture the customer's reference/proof frame locally once the recorder
  // confirms verification on PersonalDetails. No API call — this only stores the
  // image (uploaded later as proof, and reused if facematch is delegated).
  useEffect(() => {
    if (
      currentPage === "PersonalDetails" &&
      webcamOn &&
      agreedData &&
      (capturedImage === null || capturedImage === undefined)
    ) {
      (async () => {
        const base64Data = await captureImage(500, 400);
        setCapturedImage("data:image/jpeg;base64," + base64Data);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, webcamOn, agreedData, capturedImage]);

  // Delegated fallback: when the recorder asks (DELEGATE_API), run ONLY that API
  // locally until it succeeds, then notify the recorder and stop.
  useEffect(() => {
    if (!delegatedApi?.delegate || isMeetingEnded) return;
    // Ignore a delegation that was persisted for a previous page.
    if (delegatedApi.currentPage !== currentPage) return;
    const apiName = delegatedApi.apiName;
    let cancelled = false;

    (async () => {
      while (!cancelled) {
        if (abortRef.current?.signal?.aborted) break;
        const { success, outcome } =
          await faceVerification.runDelegatedApi(apiName);
        if (cancelled) break;
        // Count the delegated run too: a persistent failure that got offloaded
        // to the client must still advance the journey-wide fraud counters,
        // otherwise delegation could stall a real impersonation below threshold.
        console.log(
          `%c ⟳ delegated %c ${apiName} = ${outcome} `,
          "color:#fff;background:#7c3aed;padding:2px 6px;border-radius:3px 0 0 3px;font-weight:bold",
          "color:#5b21b6;background:#ede9fe;padding:2px 6px;border-radius:0 3px 3px 0",
        );
        recordVerificationOutcome(apiName, outcome);
        if (success) {
          pubsubPublishMethod(
            delegatedApiSuccessPublish,
            "DelegatedApiSuccess",
            // Deliberately NOT persisted, for the same reason as
            // IDENTITY_RESULT above. A handback clears a check, so a persisted
            // success gets replayed to the recorder on its next mount and
            // clears that check again on a page it was never run for. There is
            // no late-joiner to cover: delegation only happens after the
            // recorder's pipeline has already run, so it is mounted and
            // subscribed — and a recorder that DOES remount starts with no
            // delegation at all and simply resumes the check itself.
            { persist: false },
            { apiName, succeeded: true, currentPage },
          );
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 5000));
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delegatedApi, isMeetingEnded]);

  // Show verification error toasts to the customer (driven by recorder publishes).
  useEffect(() => {
    if (faceDetectError == null) return;
    const message =
      faceDetectError?.err === "NO_FACE_DETECTED"
        ? "No face detected. Please ensure your face is clearly visible."
        : faceDetectError?.err === "FACE_MATCH_UNSUCESSFUL"
          ? "Face Match Unsuccessful!"
          : faceDetectError?.err === "MULTIPLE_FACES_DETECTED"
            ? "Multiple faces detected. Please make sure only one person is in view!"
            : faceDetectError?.err === "SPOOF_DETECTED"
              ? "Make sure a real person is in view and looking towards camera in a well-lit area!"
              : faceDetectError?.err === "NO_CAMERA_PERMISSIONS"
                ? "You don't have camera permissions, Please give permissions manually"
                : "";

    if (message && !isAbortSignal) {
      handleToastMessage({ message: t(message) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faceDetectError, isAbortSignal]);

  // Once the face is verified, clear any lingering error toasts.
  useEffect(() => {
    if (faceVerified?.isVerified) {
      toast.dismiss();
    }
  }, [faceVerified]);

  const zeroBitrateCountRef = useRef(0);
  const networkToastShownRef = useRef(false);
  const webcamWasOnRef = useRef(false);
  const webcamDroppedRef = useRef(false);

  const showNetworkUnstableToast = () => {
    new Audio(`https://cdn.videosdk.live/icici/error_1.wav`).play();
    toast.error(
      t("Your network seems unstable. Please move to a better network."),
      {
        toastId: NETWORK_TOAST_ID,
        position: "top-right",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
        style: { fontFamily: fontFamilyCSS },
      },
    );
  };

  useEffect(() => {
    if (!webcamOn || !getVideoStats) return;

    const interval = setInterval(async () => {
      try {
        const stats = await getVideoStats();
        const bitrate = stats && stats.length ? (stats[0]?.bitrate ?? 0) : 0;
        console.log(
          `[Client] video bitrate: ${bitrate} (low if < ${LOW_BITRATE_KBPS})`,
        );

        if (bitrate < LOW_BITRATE_KBPS) {
          if (zeroBitrateCountRef.current >= 5) {
            zeroBitrateCountRef.current = 0;
          }
          zeroBitrateCountRef.current += 1;
          console.log(
            `[Client] low-bitrate streak: ${zeroBitrateCountRef.current}/5`,
          );
          if (
            zeroBitrateCountRef.current >= 5 &&
            !networkToastShownRef.current
          ) {
            networkToastShownRef.current = true;
            setIsNetworkUnstable(true);
            console.warn(
              "[Client] video bitrate low for 5s — prompting customer to switch network",
            );
            showNetworkUnstableToast();
          }
        } else if (
          zeroBitrateCountRef.current > 0 ||
          networkToastShownRef.current
        ) {
          zeroBitrateCountRef.current = 0;
          if (networkToastShownRef.current) {
            networkToastShownRef.current = false;
            setIsNetworkUnstable(false);
            console.log(
              "[Client] video bitrate recovered — dismissing network toast",
            );
            toast.dismiss(NETWORK_TOAST_ID);
          }
        }
      } catch (err) {
        console.error("[Client] failed to read video stats", err);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      zeroBitrateCountRef.current = 0;
      networkToastShownRef.current = false;
      setIsNetworkUnstable(false);
      toast.dismiss(NETWORK_TOAST_ID);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, webcamOn]);

  useEffect(() => {
    if (webcamOn) {
      if (webcamDroppedRef.current) {
        webcamDroppedRef.current = false;
        console.log("[Client] webcam track recovered");
        setIsNetworkUnstable(false);
        networkToastShownRef.current = false;
        toast.dismiss(NETWORK_TOAST_ID);
      }
      webcamWasOnRef.current = true;
    } else if (webcamWasOnRef.current) {
      // Track fully torn down — no camera feed at all (the bitrate watchdog is
      // stopped while the webcam is off, so it can't surface the toast). Show
      // the "Reconnecting" placeholder (render), the "network unstable" toast,
      // and disable Start until the webcam comes back.
      // console.warn(
      //   "[Client] webcam track dropped — showing reconnecting + network toast",
      // );
      // webcamDroppedRef.current = true;
      // setIsNetworkUnstable(true);
      // networkToastShownRef.current = true;
      // showNetworkUnstableToast();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webcamOn]);

  const handleToastMessage = ({ message }) => {
    new Audio(`https://cdn.videosdk.live/icici/error_1.wav`).play();
    toast.error(message, {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
      transition: Bounce,
      style: { fontFamily: fontFamilyCSS },
    });
  };

  const videoStream = useMemo(() => {
    if (webcamOn && webcamStream) {
      const mediaStream = new MediaStream();
      mediaStream.addTrack(webcamStream.track);
      return mediaStream;
    }
    return null;
  }, [webcamStream, webcamOn]);

  useEffect(() => {
    if (micRef.current) {
      if (micOn && micStream) {
        try {
          const mediaStream = new MediaStream();
          mediaStream.addTrack(micStream.track);
          micRef.current.srcObject = mediaStream;
          micRef.current
            .play()
            .catch((error) =>
              console.error("micRef.current.play() failed", error),
            );
        } catch (error) {
          console.error("Error handling mic stream:", error);
          micRef.current.srcObject = null;
        }
      } else {
        micRef.current.srcObject = null;
      }
    }
  }, [micStream, micOn]);

  useEffect(() => {
    if (videoRef.current) {
      if (webcamOn && videoStream) {
        try {
          videoRef.current.srcObject = videoStream;
          videoRef.current.setAttribute("playsinline", "");
          videoRef.current.setAttribute("x5-playsinline", "");
          videoRef.current.setAttribute("webkit-playsinline", "");
          videoRef.current
            .play()
            .catch((error) =>
              console.error("videoRef.current.play() failed", error),
            );
        } catch (error) {
          console.error("Error handling webcam stream:", error);
          videoRef.current.srcObject = null;
        }
      } else {
        videoRef.current.srcObject = null;
      }
    }
  }, [videoStream, webcamOn]);

  const isVerified = faceVerified?.isVerified;

  const videoBoxHeight =
    !isMobileOrTab && currentPage !== "VideoConsent"
      ? "490px"
      : isTab
        ? "290px"
        : isBigDesktop && currentPage === "VideoConsent"
          ? videoHeight
          : isBigDesktop
            ? "600px"
            : isMobile
              ? "180px"
              : videoHeight
                ? videoHeight
                : "380px";
  const videoBoxWidth = isMobile
    ? "100%"
    : isTab
      ? "70%"
      : currentPage === "VideoConsent"
        ? isBigDesktop
          ? "50%"
          : isLGDesktop
            ? "100%"
            : "70%"
        : "100%";

  // PersonalDetails-style pages show the webcam in its natural landscape frame so
  // object-cover doesn't crop it into a vertical slice. On mobile we use the Figma
  // spec — a fixed 370 × 198 landscape frame, capped to the available width so it
  // never overflows a narrow phone (and an explicit height so the box can't
  // collapse to a tiny video inside the nested flex layout). On tab/desktop the
  // box keeps scaling fluidly at 16:9 via videoBoxWidth. VideoConsent keeps its
  // own custom fixed sizing (driven by the videoHeight prop).
  const videoBoxStyle =
    currentPage === "VideoConsent"
      ? { width: videoBoxWidth, height: videoBoxHeight }
      : isMobile
        ? { width: "100%", maxWidth: "370px", height: "198px" }
        : { width: videoBoxWidth, aspectRatio: "16 / 9" };

  // The device-picker row sits directly under the video and must track its width
  // exactly — including the mobile 370px cap.
  const controlsBoxStyle =
    currentPage !== "VideoConsent" && isMobile
      ? { width: "100%", maxWidth: "370px" }
      : { width: videoBoxWidth };

  return (
    <div
      className={`flex lg:flex-1 w-full flex-col items-center justify-end md:items-center relative md:justify-center`}
    >
      {webcamOn ? (
        <div
          className="relative"
          style={videoBoxStyle}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={true}
            className={`h-full w-full ${
              isVerified
                ? agreedData
                  ? "border-2 border-green-600"
                  : "border-2 border-red-700"
                : ""
            }`}
            style={{  borderRadius: 16 }}
          />

          {!isVerified && (
            <>
              <div className="absolute inset-0 z-30 rounded-xl bg-[#16161680]" />
              <div className="absolute inset-0 z-40 flex items-center justify-center p-4">
                <p className="mb-0 text-center text-xl text-white">
                  {`${t("Please hold on while we verify your video")}...`}
                </p>
              </div>
            </>
          )}

          {isVerified && agreedData && (
            <div className="absolute bottom-3 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2 rounded-lg bg-[#000000B3] px-3 py-1.5 backdrop-blur-sm">
              <CheckIcon className="h-4 w-4 text-green-400 lg:h-5 lg:w-5" />
              <p className="mb-0 whitespace-nowrap text-sm text-white lg:text-base">
                {t("Face detected")}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div
          style={videoBoxStyle}
          className="flex flex-col items-center justify-center gap-4 rounded-xl bg-[#161616]"
        >
          <div
            style={{ borderTopColor: "#EA7617" }}
            className="animate-spin ease-linear rounded-full border-4 border-gray-100 h-12 w-12"
          ></div>
          <p
            style={{ fontFamily: fontFamilyCSS }}
            className="text-white text-base lg:text-lg text-center px-4 mb-0"
          >
            {`${t("Reconnecting")}...`}
          </p>
        </div>
      )}

      {/* Device picker row — sits directly under the video, same width as it. */}
      <div style={controlsBoxStyle} className="mt-3">
        <DeviceControls />
      </div>

      <audio ref={micRef} autoPlay playsInline muted={isLocal} />
    </div>
  );
};

export default ParticipantView;
